import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { List } from "react-window";
import { getCurrentSelectedSiteName, updateClCookie } from "../../utils/common";
import { getCookie } from "../../serviceWorker";
import { useI18n } from "../../i18n/useI18n";
import css from "../genericMagneticStyle.less";
import NoDataAvailable from "../../common/NoDataAvailable";

//assets
import hemisphere from "../../assets/globe-hemisphere-west.svg";

const [
  HbrDropdown,
  HbrButton,
  HbrInput,
  HbrIcon,
  HbrMenu,
  HbrMenuItem,
  HbrTag,
  HbrSpinner,
  HbrCheckbox,
] = reactWrapper([
  "hbr-dropdown",
  "hbr-button",
  "hbr-input",
  "hbr-icon",
  "hbr-menu",
  "hbr-menu-item",
  "hbr-tag",
  "hbr-spinner",
  "hbr-checkbox",
]);

const SiteNetworkHierarchy = (props) => {
  const { tViews } = useI18n("views");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const isUIConverged = getCookie("cl-analytics");
  const isNonSiteSelectionEnabled =
    getCookie("cl-nh-selection-enabled") === "true";
  // Initialize selectedNhName from cookie only if non-site selection is enabled
  const [selectedNhName, setSelectedNhName] = useState(
    isUIConverged && isNonSiteSelectionEnabled
      ? getCookie("cl-selected-nh-name") || ""
      : "",
  );
  const { selectedSite, selectedNhName: selectedNhNameFromStore } =
    props.globalFilter;
  let uniqueSites = useRef(new Set());
  const sitesData = props.globalFilter.sites;
  const sitesLoader = props.globalFilter.sitesListLoader;
  const checkedSites = useRef(new Set());
  const [, setSelectedSiteIds] = useState(selectedSite);
  const closeOnSelect = props.closeOnSelect === false ? false : true;
  const siteIds = props.site;
  const normalizedReportSiteIds = useMemo(
    () =>
      Array.isArray(siteIds)
        ? siteIds
            .filter(
              (siteId) =>
                siteId !== null && siteId !== undefined && siteId !== "",
            )
            .map((siteId) => siteId.toString())
        : [],
    [siteIds],
  );

  const nhNameFromStore = selectedNhNameFromStore || "";

  useEffect(() => {
    // Sync local state with store value
    // This handles both setting a value from store and clearing when store is cleared
    if (nhNameFromStore !== selectedNhName) {
      setSelectedNhName(nhNameFromStore);
    }
  }, [nhNameFromStore]);

  useEffect(() => {
    if (!props.isReport) return;

    checkedSites.current = new Set(normalizedReportSiteIds);
    setSelectedSiteIds([...normalizedReportSiteIds]);
  }, [props.isReport, normalizedReportSiteIds]);

  // Clear selectedNhName and cookie when a specific site is selected from any sites tabl (site_id > 0)
  useEffect(() => {
    if (selectedSite > 0 && selectedNhName) {
      setSelectedNhName("");
      updateClCookie("cl-selected-nh-name", null);
    }
  }, [selectedSite]);

  // Create tree object from hierarchy paths
  const createTreeFromHierarchyPaths = () => {
    let treeObject = {};
    if (sitesData.length === 0) return {};

    sitesData.forEach((item) => {
      uniqueSites.current.add(item.site_name);
      const pathParts = item.hierarchy_path.split("/");
      let currentLevel = treeObject;

      pathParts.forEach((part, index) => {
        if (!part) return;

        if (!currentLevel[part]) {
          currentLevel[part] = {
            isLeaf: index === pathParts.length - 1,
            siteData: index === pathParts.length - 1 ? item : null,
            children: {},
          };
        }

        if (index < pathParts.length - 1) {
          currentLevel = currentLevel[part].children;
        }
      });
    });

    return treeObject;
  };

  // Generate the tree structure
  const hierarchyTree = useMemo(
    () => createTreeFromHierarchyPaths(),
    [sitesData],
  );

  // Helper function to check if a node has any direct site children
  // This is used to determine if a non-site node can be selected (n-1 level restriction)
  const hasDirectSiteChildren = useCallback(
    (nodePath) => {
      const pathParts = nodePath.split("/").filter((part) => part);
      let currentLevel = hierarchyTree;

      // Navigate to the node
      for (const part of pathParts) {
        if (currentLevel && currentLevel[part]) {
          if (pathParts.indexOf(part) === pathParts.length - 1) {
            // We're at the target node, check its children
            const children = currentLevel[part].children;
            // Check if any child is a site (leaf node with siteData)
            return Object.values(children).some(
              (child) => child.isLeaf && child.siteData,
            );
          }
          currentLevel = currentLevel[part].children;
        } else {
          return false;
        }
      }
      return false;
    },
    [hierarchyTree],
  );

  // Initialize expanded nodes for first two levels
  useEffect(() => {
    const initialExpanded = new Set();

    const initializeExpanded = (treeData, parentPath = "") => {
      Object.keys(treeData).forEach((key) => {
        const currentPath = parentPath ? `${parentPath}/${key}` : key;
        const node = treeData[key];
        const hasChildren = Object.keys(node.children).length > 0;

        if (hasChildren) {
          initialExpanded.add(currentPath);
          initializeExpanded(node.children, currentPath);
        }
      });
    };

    initializeExpanded(hierarchyTree);
    setExpandedNodes(initialExpanded);
  }, [hierarchyTree]);

  // Create a flattened list for virtualization with improved structure
  const createFlatTreeList = () => {
    const flatList = [];

    const traverse = (
      treeData,
      depth = 0,
      parentPath = "",
      parentExpanded = true,
    ) => {
      Object.keys(treeData).forEach((key) => {
        const node = treeData[key];
        const hasChildren = Object.keys(node.children).length > 0;
        const isSite = node.isLeaf && node.siteData;
        const currentPath = parentPath ? `${parentPath}/${key}` : key;
        const isExpanded = expandedNodes.has(currentPath);

        const flatItem = {
          key,
          depth,
          hasChildren,
          isSite,
          path: currentPath,
          expanded: isExpanded,
          siteData: node.siteData,
          children: hasChildren ? Object.keys(node.children).length : 0,
          visible: parentExpanded, // Only visible if parent is expanded
        };

        flatList.push(flatItem);

        if (hasChildren && isExpanded && parentExpanded) {
          traverse(node.children, depth + 1, currentPath, true);
        }
      });
    };

    traverse(hierarchyTree);

    // Filter based on search term
    if (searchTerm) {
      return flatList.filter(
        (item) =>
          item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.path.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return flatList.filter((item) => item.visible);
  };

  const flatTreeList = useMemo(
    () => createFlatTreeList(),
    [hierarchyTree, searchTerm, expandedNodes],
  );
  const getTreeRowKey = useCallback(
    (index) => flatTreeList[index].path,
    [flatTreeList],
  );

  // Handle node expand/collapse
  const toggleNodeExpansion = (nodePath) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodePath)) {
        newSet.delete(nodePath);
      } else {
        newSet.add(nodePath);
      }
      return newSet;
    });
  };

  const getIsReportSelectionDisabled = (item) => {
    const siteIdStr = item?.siteData?.site_id?.toString();
    const isReportMode =
      props.isReport === true && item?.isSite && item?.siteData;
    return (
      isReportMode &&
      (props.disabled ||
        (!checkedSites.current.has(siteIdStr) &&
          checkedSites.current.size >= 5))
    );
  };

  // Row renderer for virtualized tree
  const TreeRow = ({ ariaAttributes, index, style }) => {
    const item = flatTreeList[index];
    if (!item) return null;

    let paddingLeft = 0;

    if (item.depth > 0) {
      paddingLeft = 6;
    }

    // Allow non-site selection only for nodes directly above sites.
    const canSelectNonSiteNode =
      isUIConverged &&
      isNonSiteSelectionEnabled &&
      hasDirectSiteChildren(item.path);
    const isDisabledNode =
      !item.isSite && item.key !== "Global" && !canSelectNonSiteNode;

    const getIcon = () => {
      if (item.isSite) {
        return <HbrIcon name="city" size="16px" className="site-icon" />;
      }
      if (item.depth === 0) {
        return <HbrIcon name="globe" size="16px" className="domain-icon" />;
      }
      if (item.depth === 1) {
        return <HbrIcon src={hemisphere} size="16px" className="city-icon" />;
      }
      if (item.depth === 2) {
        return <HbrIcon src={hemisphere} size="16px" className="area-icon" />;
      }
      return <HbrIcon name="folder" size="16px" className="building-icon" />;
    };

    const handleClick = () => {
      if (props.isReport === true) {
        props.handleSiteSelect(item.site_id);
      } else if (item.isSite && item.siteData) {
        if (selectedSite.toString() !== item.siteData.site_id.toString()) {
          // Clear nhName cookie when selecting a site
          updateClCookie("cl-selected-nh-name", null);
          setSelectedNhName("");
          props.setSelectedSite && props.setSelectedSite(item.siteData.site_id);
        }
      } else if (item.key === "Global") {
        // Clear nhName cookie when selecting Global
        updateClCookie("cl-selected-nh-name", null);
        setSelectedNhName("");
        props.setSelectedSite && props.setSelectedSite(-1);
      } else if (canSelectNonSiteNode && !item.isSite) {
        // Handle non-site node selection (e.g., Metros, NonMetros)
        // Only allow if this node has direct site children (n-1 restriction)
        updateClCookie("cl-selected-nh-name", item.key);
        setSelectedNhName(item.key);
        props.setSelectedSite && props.setSelectedSite(-1);
      }
    };

    const handleExpandClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleNodeExpansion(item.path);
    };

    const siteIdStr = item.siteData?.site_id?.toString();
    const isReportSelectionDisabled = getIsReportSelectionDisabled(item);

    const onCheckboxChange = (event) => {
      event.stopPropagation();
      let valueToUse = event.currentTarget.id;

      const detail = event.detail ? event.detail : event.currentTarget;
      const isChecked = detail.checked
        ? detail.checked
        : event.currentTarget.checked;

      if (isReportSelectionDisabled) return;

      if (isChecked) {
        checkedSites.current.add(valueToUse);
      } else {
        checkedSites.current.delete(valueToUse);
      }
      setSelectedSiteIds(Array.from(checkedSites.current));
    };

    // Check if this item is selected (either a site or a non-site hierarchy node)
    const isSiteSelected =
      item.isSite &&
      item.siteData &&
      selectedSite !== null &&
      selectedSite !== -1 &&
      selectedSite.toString() === item.siteData.site_id.toString() &&
      !selectedNhName;
    const isNonSiteNodeSelected =
      !item.isSite && item.key !== "Global" && selectedNhName === item.key;
    const isGlobalSelected =
      item.key === "Global" && selectedSite === -1 && !selectedNhName;
    const isSelected =
      isSiteSelected || isNonSiteNodeSelected || isGlobalSelected;

    // ✅ Helper function to render vertical lines based on depth
    const renderVerticalLines = () => {
      if (item.key === "Global") return null;

      if (item.hasChildren) {
        // Render lines for parent nodes with children
        if (item.depth === 1) {
          return (
            <div className="vertical-line-container">
              <div className="line-site" />
            </div>
          );
        }
        if (item.depth === 2) {
          return (
            <div className="vertical-line-container">
              <div className="line-global" />
              <div className="line-site-child" />
            </div>
          );
        }
        if (item.depth === 3) {
          return (
            <div className="vertical-line-container">
              <div className="line-global" />
              <div className="line-area" />
              <div className="line-site-child" />
            </div>
          );
        }
      } else {
        // Render lines for leaf nodes (sites)
        if (item.depth === 1) {
          // Global -> Site (2 levels)
          return (
            <div className="vertical-line-container">
              <div className="line-site-child" />
            </div>
          );
        }
        if (item.depth === 2) {
          // Global -> Region -> Site (3 levels)
          return (
            <div className="vertical-line-container">
              <div className="line-area" />
              <div className="line-site" />
            </div>
          );
        }
        if (item.depth === 3) {
          // Global -> Region -> Subregion -> Site (4 levels)
          return (
            <div className="vertical-line-container">
              <div className="line-global" />
              <div className="line-area" />
              <div className="line-site" />
            </div>
          );
        }
        if (item.depth === 4) {
          // ✅ Global -> Region -> Subregion -> Area -> Site (5 levels)
          // Example: Global/India/South/Karnataka/Bengaluru
          return (
            <div className="vertical-line-container">
              <div className="line-global" />
              <div className="line-area" />
              <div className="line-site" />
              <div className="line-site-child" />
            </div>
          );
        }
      }

      return null;
    };

    return (
      <HbrMenuItem
        {...ariaAttributes}
        style={style}
        value={item.siteData?.site_id ? item.siteData.site_id : -1}
        className={`site-network-hierarchy-item ${isSelected ? "selected-item" : ""} ${isDisabledNode ? "disabled-node" : ""}`}
        onClick={(e) => onMenuItemClick(e, item)}
        checkMark={isSelected}
        checked={isSelected}
      >
        <div
          className={`tree-row ${item.isSite ? "site-row" : "folder-row"} ${isSelected ? "selected" : ""} ${isDisabledNode ? "disabled-node" : ""}`}
          style={{
            paddingLeft: `${paddingLeft}px`,
            cursor: isDisabledNode
              ? "not-allowed"
              : item.isSite || item.key === "Global" || canSelectNonSiteNode
                ? "pointer"
                : "default",
            borderLeft: "3px solid transparent",
          }}
        >
          {item.hasChildren && (
            <>
              {renderVerticalLines()}
              <div
                className="border-left-vertical-line"
                style={{
                  left: `${paddingLeft - 8}px`,
                }}
              ></div>
              <div
                onClick={handleExpandClick}
                className="caret-icon-container"
                style={{ position: "relative" }}
              >
                <HbrIcon
                  name={item.expanded ? "caret-down" : "caret-right"}
                  size="16px"
                  sentiment="neutral"
                />
              </div>
            </>
          )}
          {/* ✅ Render vertical lines for leaf nodes */}
          {!item.hasChildren && renderVerticalLines()}
          {!item.hasChildren && props.isReport === true && (
            <HbrCheckbox
              id={item.siteData.site_id}
              checked={
                checkedSites.current.has(siteIdStr) ||
                normalizedReportSiteIds.includes(siteIdStr)
              }
              disabled={isReportSelectionDisabled}
            ></HbrCheckbox>
          )}
          {getIcon()}
          <span
            className={`tree-label hbr-type-body3 ${isDisabledNode ? "disabled-label" : ""}`}
            onClick={onCheckboxChange}
          >
            {item.key}
          </span>
          {item.hasChildren && (
            <HbrTag className="tree-tag" handleClick={handleClick}>
              {item.children}
            </HbrTag>
          )}
        </div>
      </HbrMenuItem>
    );
  };

  TreeRow.propTypes = {
    ariaAttributes: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    style: PropTypes.object.isRequired,
  };

  // Use virtualized tree for better performance
  const renderTree = () => {
    const listHeight = Math.min(321, flatTreeList.length * 32);

    if (flatTreeList.length === 0) {
      return (
        <div className="no-data-message flex-column flex-items-center ">
          <HbrIcon
            size="65px"
            name="hbr-empty-state"
            aria-label={tViews("a11yLabels.noMatchesFound")}
          ></HbrIcon>
          <span className="hbr-css__text-secondary-sm">
            {tViews("a11yLabels.noMatchesFound")}
          </span>
        </div>
      );
    }

    return (
      <div className="virtual-tree-container-wrapper">
        <List
          rowComponent={TreeRow}
          rowCount={flatTreeList.length}
          rowHeight={32}
          rowKey={getTreeRowKey}
          rowProps={{}}
          style={{ height: listHeight, width: "100%" }}
        />
      </div>
    );
  };

  // Get the display name for the selected site
  const siteName = useMemo(() => {
    // Check if a non-site hierarchy node is selected
    if (selectedNhName) return selectedNhName;

    if (selectedSite === -1) return tViews("appHocAllSites");

    const site = sitesData.find((item) => item.site_id === selectedSite);
    return site ? site.site_name : "";
  }, [selectedSite, sitesData, selectedNhName]);

  // Handle search functionality
  const onSearch = (e) => {
    const searchText = e.target.value;
    setSearchTerm(searchText);
  };

  useEffect(() => {
    // Set site_selected value for Qualtrics metadata
    window._site_selected = getCurrentSelectedSiteName(props.globalFilter);
  }, [selectedSite, nhNameFromStore]);

  const onMenuItemClick = useCallback(
    (event, item) => {
      const { path, isSite, key } = item;

      // Allow non-site selection only for nodes directly above sites.
      const canSelectNonSiteNode =
        isUIConverged &&
        isNonSiteSelectionEnabled &&
        hasDirectSiteChildren(path);

      // clear the redux store attribute on site change
      const value = event.currentTarget.value;
      if (
        event.target.name === "caret-down" ||
        event.target.name === "caret-right"
      ) {
        event.preventDefault();
        event.stopPropagation();
        if (
          event.target.name === "caret-down" ||
          event.target.name === "caret-right"
        ) {
          toggleNodeExpansion(path);
        }
      } else if (!isSite && key !== "Global" && !canSelectNonSiteNode) {
        // Disable clicks for non-selectable hierarchy nodes (n-1 restriction)
        // Only allow non-site selection if the node has direct site children
        event.preventDefault();
        event.stopPropagation();
        return;
      } else if (!isSite && key !== "Global" && canSelectNonSiteNode) {
        // Handle non-site node selection (e.g., Metros, NonMetros)
        // Only allowed if this node has direct site children (n-1 restriction)
        updateClCookie("cl-selected-nh-name", key);
        setSelectedNhName(key);
        props.setSelectedSite && props.setSelectedSite(-1);
      } else if (props.isReport === true && value !== "-1") {
        const isReportSelectionDisabled = getIsReportSelectionDisabled(item);

        if (isReportSelectionDisabled) return;

        let valueToUse = event.currentTarget.value;

        if (checkedSites.current.has(valueToUse)) {
          checkedSites.current.delete(valueToUse);
        } else {
          checkedSites.current.add(valueToUse);
        }
        setSelectedSiteIds(Array.from(checkedSites.current));

        props.handleSiteSelect(Array.from(checkedSites.current));
      } else if (item.key === "Global") {
        // Clear nhName cookie when selecting Global
        updateClCookie("cl-selected-nh-name", null);
        setSelectedNhName("");
        props.setSelectedSite && props.setSelectedSite(-1);
      } else if (
        value?.toString() &&
        selectedSite?.toString() !== value?.toString()
      ) {
        // Clear nhName cookie when selecting a site
        updateClCookie("cl-selected-nh-name", null);
        setSelectedNhName("");
        props.setSelectedSite(value);
      }
    },
    [selectedSite, hasDirectSiteChildren],
  );

  return (
    <div
      className={css["site-network-hierarchy"]}
      data-cy="site-network-hierarchy"
    >
      <HbrDropdown menu={true} multiple={true} closeOnSelect={closeOnSelect}>
        <HbrButton
          slot="trigger"
          variant="outline"
          sentiment="neutral"
          caret
          aria={{ label: tViews("a11yLabels.allSites") }}
          role="button"
        >
          {props.isReport === true ? (
            <div
              aria-label={`${tViews("a11yLabels.siteHierarchySelectedSite")} ${checkedSites.current.size === 0 ? tViews("workflowAllCustomSite") : `${checkedSites.current.size === 1 ? tViews("siteValue", { count: checkedSites.current.size }) : tViews("sitesValue", { count: checkedSites.current.size })} `}`}
              role="text"
            >
              {checkedSites.current.size === 0
                ? tViews("workflowAllCustomSite")
                : `${checkedSites.current.size === 1 ? tViews("siteValue", { count: checkedSites.current.size }) : tViews("sitesValue", { count: checkedSites.current.size })}`}
            </div>
          ) : (
            <>
              <HbrIcon
                slot="prefix"
                name="nav-domains"
                weight="bold"
                size="16px"
                className="globe-icon"
                aria-label={tViews("a11yLabels.siteHierarchyGlobal")}
              />
              <div
                aria-label={`${tViews("a11yLabels.siteHierarchySelectedSite")} ${siteName}`}
                role="text"
              >
                {siteName}
              </div>
            </>
          )}
        </HbrButton>
        <HbrMenu
          role="menu"
          aria-label={tViews("a11yLabels.siteHierarchySiteMenu")}
        >
          <div
            className="site-menu-container"
            role="group"
            aria-label={tViews("a11yLabels.siteHierarchySiteMenuContainer")}
          >
            <HbrInput
              className="sites-container hbr-css__form-section-md"
              size="medium"
              placeholder={tViews("a11yLabels.megaMenuSearchSite")}
              clearable
              onHbr-input={onSearch}
              aria={{
                label: `${tViews("a11yLabels.megaMenuSearchSite")} ${tViews("a11yLabels.allSites")}`,
              }}
              role="searchbox"
            >
              <div
                slot="prefix"
                role="img"
                aria-label={tViews("a11yLabels.siteHierarchySearchIcon")}
              >
                <HbrIcon sentiment="neutral" name="magnifying-glass" />
              </div>
            </HbrInput>
            <div
              className="virtual-tree-container"
              role="tree"
              aria-label={tViews(
                "a11yLabels.siteHierarchyVirtualTreeContainer",
              )}
            >
              {Object.keys(hierarchyTree).length > 0 && renderTree()}
            </div>
            {props.globalFilter.sites.length === 0 && sitesLoader === false && (
              <div className="no-data-available flex-column flex-items-center">
                <NoDataAvailable />
              </div>
            )}
            {sitesLoader === true && (
              <div className="loading-spinner flex-column flex-items-center">
                <HbrSpinner size="medium" />
              </div>
            )}
          </div>
        </HbrMenu>
      </HbrDropdown>
    </div>
  );
};
SiteNetworkHierarchy.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  setSelectedSite: PropTypes.func,
  index: PropTypes.number,
  style: PropTypes.object,
  isReport: PropTypes.bool,
  handleSiteSelect: PropTypes.func,
  closeOnSelect: PropTypes.bool,
  site: PropTypes.array,
  disabled: PropTypes.bool,
};
export default SiteNetworkHierarchy;
